import { IDataFlowDataType, ArrayDataFlowType, getDataFlowExpressionBuilderTypeIcon, AnyDataFlowType } from 'app/model/dataflow/dataflow-datatype';
import { TreeNode } from 'primeng/api';
import { IExpressionBasedEntity } from 'app/model/dataflow/IExpressionBasedEntity';

import { ComplexDataFlowType } from 'app/model/dataflow/dataflow-complex-datatype';
import { DataFlowColumn, IBasedOnEntity } from 'app/model/dataset/dataflowcolumn';
import { IExtraFieldInfo } from 'app/interfaces/IExtraFieldInfo';
import { DataFlowUtils } from './dataFlow-utils';
import { createGetter, IGetterResult } from 'app/shared/utils/object.util';
import { ColumnReference } from 'app/model/dataset/columnReference';
import { DataFlowTypesUtils } from 'app/model/dataflow/dataflow-type-utilities';
import { Constants } from 'app/shared/shared-constants';
import { IRadioOption } from 'app/interfaces/IRadioOption';
import { DataFlowSchemaAdapterUtils } from 'app/model/adapters/dataflow/dataflow-schema-adapter-utils';

enum DocumentForm {
    singleDocument = 'singleDocument',
    documentPerLine = 'documentPerLine',
    arrayOfDocuments = 'arrayOfDocuments'
}

export const documentFormOptions: Array<IRadioOption<DocumentForm>> = [
    {
        label: stringResources.DataFlowSourceSettingsDocumentFormSingleLabel,
        value: DocumentForm.singleDocument
    },
    {
        label: stringResources.DataFlowSourceSettingsDocumentFormPerLineLabel,
        value: DocumentForm.documentPerLine
    },
    {
        label: stringResources.DataFlowSourceSettingsDocumentFormArrayLabel,
        value: DocumentForm.arrayOfDocuments
    }
];

export interface ITreeNodeOptionData {
    name: string;
    type?: IDataFlowDataType;
    insertedText?: string;
    struct?: string;
    isArray?: boolean;
    columnRef?: ColumnReference;
    invalidColumnName?: string;
    item?: IExpressionBasedEntity;
}

export interface ITreeNodeOption extends TreeNode {
    data: ITreeNodeOptionData;
}

export interface ITreeNodeInspectData {
    order: number;
    column: DataFlowColumn;
    columnName: string;
    columnType: string;
    isArray: boolean;
    columnTypeFormat: string;
    editable?: boolean;
}

export interface ITreeNodeInspect extends TreeNode {
    data: ITreeNodeInspectData;
}

export class ComplexTypeUtils {
    public static generateHierarchicalArgumentsGetter(getAvailableColumns: () => ColumnReference[], getCacheId: () => string,
        expanded: boolean = false, includeArrayIndex: boolean = true, allowedPath?: () => string, hasNoneOption?: boolean): IGetterResult<ITreeNodeOption[]> {
        return createGetter(() => {
            const availableColumns = getAvailableColumns();
            const options = availableColumns.map((columnRef) => {
                const label = !availableColumns.find((otherColumnRef) => otherColumnRef !== columnRef && otherColumnRef.column.name === columnRef.column.name)
                    ? columnRef.column.name : columnRef.name;
                return columnRef.column.dataFlowColumnType ? ComplexTypeUtils.generateHierarchyNode(label, columnRef.column.dataFlowColumnType, undefined, columnRef.originalName, null,
                    undefined, expanded, columnRef, includeArrayIndex, allowedPath) : undefined;
            }).filter((option) => !!option);
            if (hasNoneOption) {
                options.unshift({ label: stringResources.None, data: { name: 'none', insertedText: '' }, icon: ' ' });
            }
            return options;
        }, getCacheId);
    }

    public static generateHierarchicalArguments(fields: IExpressionBasedEntity[], prefix: string, parent: ITreeNodeOption, expressionFilter?: string,
        expanded: boolean = false, columnRef?: ColumnReference, includeArrayIndex: boolean = true, allowedPath?: () => string, includeStruct?: boolean,
        allTreeArguments?: TreeNode[]): ITreeNodeOption[] {
        const children: ITreeNodeOption[] = [];
        fields.forEach((field) => {
            children.push(this.generateHierarchyNode(field.entityName, field.dataFlowExpression.returnType, prefix,
                undefined, parent, expressionFilter, expanded, columnRef, includeArrayIndex, allowedPath, includeStruct, field, false, allTreeArguments));
        });
        return children;
    }

    // This functions generates the hierarchy nodes for expression builder
    public static generateHierarchyNode(nodeName: string, dataflowType: IDataFlowDataType, prefix: string, insertedText: string, parent: ITreeNodeOption,
        expressionFilter?: string, expanded: boolean = false, baseColumnRef?: ColumnReference, includeArrayIndex: boolean = true,
        allowedPath?: () => string, includeStruct?: boolean, item?: IExpressionBasedEntity, isTransient?: boolean, allTreeArguments?: TreeNode[]): ITreeNodeOption {
        const escapedName = DataFlowUtils.needToEscape(nodeName) ? `{${nodeName}}` : nodeName;
        const fullName = `${prefix ? `${prefix}.` : ''}${escapedName}`;
        const actualType = dataflowType instanceof ArrayDataFlowType ? dataflowType.arrayType : dataflowType;
        const newPrefix = `${isTransient ? ':' : ''}${fullName}${dataflowType instanceof ArrayDataFlowType && includeArrayIndex ? '[1]' : ''}`;
        const fieldPath = insertedText || fullName;
        if (allowedPath && !allowedPath().startsWith(fieldPath)) {
            return null;
        }
        let columnRef = baseColumnRef;
        if (baseColumnRef && (actualType instanceof ComplexDataFlowType || (baseColumnRef.column && baseColumnRef.column.dataFlowColumnType instanceof ComplexDataFlowType))) {
            columnRef = null;
            columnRef = new ColumnReference(baseColumnRef.column);
            columnRef.isAmbiguous = baseColumnRef.isAmbiguous;
            columnRef.complexFieldPath = fieldPath;
        }
        const node: ITreeNodeOption = {
            data: {
                name: fullName,
                type: dataflowType,
                insertedText: fieldPath,
                isArray: dataflowType instanceof ArrayDataFlowType,
                columnRef,
                item
            },
            label: `${nodeName}${dataflowType instanceof ArrayDataFlowType ? ' []' : ''}`,
            children: null,
            parent
        };
        const existingTreeArgument = allTreeArguments && allTreeArguments.length && allTreeArguments.find((nodeToFind: TreeNode) => nodeToFind.data.item === node.data.item);
        node.expanded = existingTreeArgument ? existingTreeArgument.expanded : expanded;
        node.children = (actualType instanceof ComplexDataFlowType
            ? this.generateHierarchicalArguments(actualType.fields, newPrefix, node, expressionFilter, expanded, baseColumnRef, includeArrayIndex, allowedPath, includeStruct, allTreeArguments) : [])
            .filter((child) => !!child);
        if (node.children.length) {
            node.type = 'struct';
            node.data.struct = includeStruct ? `@(${node.children.map((child) => {
                let childLabel = !child.data.isArray ? child.label : child.label.substring(0, child.label.length - 2);
                childLabel = DataFlowUtils.needToEscape(childLabel) ? `{${childLabel}}` : childLabel;
                return `${childLabel} = ${child.data.struct}`;
            }).join(', ')})` : '';
        } else {
            node.data.struct = includeStruct ? fullName : '';
        }
        node.icon = actualType instanceof ComplexDataFlowType && node.children.length ? '' : getDataFlowExpressionBuilderTypeIcon(actualType.typeString);
        return !expressionFilter || nodeName.toLowerCase().indexOf(expressionFilter) > -1 || node.children.length ? node : null;
    }

    public static uniqueComplexFieldName(fields: IExpressionBasedEntity[]): string {
        const prefix = stringResources.ColumnMappingFieldHeader.toLowerCase();
        if (!fields) {
            return prefix;
        }
        const regex = new RegExp('^' + prefix + '[0-9]+$');

        let maxNumber = 0;
        fields.forEach((field) => {
            if (field && field.entityName.match(regex)) {
                // eslint-disable-next-line radix
                const thisSuffix = Number.parseInt(field.entityName.slice(prefix.length));
                if (thisSuffix > maxNumber) {
                    maxNumber = thisSuffix;
                }
            }
        });

        return prefix.concat((maxNumber + 1).toString());
    }

    // This functions generates the hierarchy nodes for schemas
    public static generateHierarchySchema(dataflowColumns: DataFlowColumn[], extraColumns: IExtraFieldInfo[] = []): TreeNode[] {
        return dataflowColumns.map((column: DataFlowColumn, index: number) => {
            return ComplexTypeUtils.populateHierarchyNode(column, column.name, column.dataFlowColumnType, index, extraColumns);
        });
    }

    public static populateHierarchySchema(fields: IExpressionBasedEntity[], extraColumns: IExtraFieldInfo[] = [], prefix: string): TreeNode[] {
        const children: TreeNode[] = [];
        fields.forEach((field) => {
            children.push(ComplexTypeUtils.populateHierarchyNode(null, field.entityName, field.dataFlowExpression && field.dataFlowExpression.returnType, -1, extraColumns, prefix));
        });
        return children;
    }

    public static populateHierarchyNode(column: DataFlowColumn, fieldName: string, fieldType: IDataFlowDataType, index: number = -1,
        extraColumns: IExtraFieldInfo[] = [], prefix?: string): TreeNode {
        const actualType = fieldType instanceof ArrayDataFlowType ? fieldType.arrayType : fieldType;
        const fieldFullName = `${prefix ? `${prefix}.` : ''}${fieldName}`;
        const node: ITreeNodeInspect = {
            data: {
                order: index + 1,
                column,
                columnName: fieldName,
                columnType: actualType ? actualType.typeString : '',
                isArray: fieldType instanceof ArrayDataFlowType,
                columnTypeFormat: fieldType && fieldType.format ? fieldType.format : (column && column.columnTypeFormat)
            },
            children: actualType instanceof ComplexDataFlowType ? this.populateHierarchySchema(actualType.fields, extraColumns, fieldFullName) : []
        };
        extraColumns.forEach((field) => {
            if (column || field.includeFields) {
                // for fields we generate the object here to include the full name i.e. 'orders.trade' instead of just 'trade'
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const columnParam: any = column || { name: fieldFullName };
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (node.data as any)[field.name()] = field.value ? field.value(columnParam) : field.icon && field.icon(columnParam);
            }
        });
        return node;
    }

    public static getFieldDataType(baseEntity: IBasedOnEntity, fieldPath: string, unrollPath: string = ''): IDataFlowDataType {
        let dataFlowType: IDataFlowDataType = baseEntity ? baseEntity.dataFlowColumnType : new AnyDataFlowType();
        let typeFound = true;
        let unFoldedArray = false;
        if (dataFlowType instanceof ArrayDataFlowType && (fieldPath !== baseEntity.name || (unrollPath && unrollPath.startsWith(fieldPath)))) {
            dataFlowType = dataFlowType.arrayType;
        }
        if (dataFlowType instanceof ComplexDataFlowType) {
            const fieldNames = ComplexTypeUtils.splitComplexFieldPath(fieldPath);
            const unrollFieldNames = ComplexTypeUtils.splitComplexFieldPath(unrollPath);
            let useArrayType = unrollPath && unrollPath.startsWith(fieldPath);
            for (let fieldIndex = 1; fieldIndex < fieldNames.length; fieldIndex++) {
                let currentFieldName = fieldNames[fieldIndex];
                if (Constants.arrayIndexRegex.test(currentFieldName)) {
                    currentFieldName = currentFieldName.replace(Constants.arrayIndexRegex, '');
                    useArrayType = true;
                }
                const foundField = dataFlowType instanceof ComplexDataFlowType && dataFlowType.fields.find((field) => {
                    let entityName = field.entityName;
                    if (Constants.dataFlowEscapedNameValidation.test(field.entityName)) {
                        entityName = DataFlowSchemaAdapterUtils.removeWrappingCharacters(field.entityName);
                    }
                    return (entityName === currentFieldName) ? field : undefined;
                });
                if (!foundField) {
                    typeFound = false;
                    break;
                }
                dataFlowType = foundField.dataFlowExpression.returnType;
                if (dataFlowType instanceof ArrayDataFlowType) {
                    if (useArrayType || fieldIndex < fieldNames.length - 1) {
                        dataFlowType = dataFlowType.arrayType;
                    }
                    if (unrollPath && fieldPath.startsWith(unrollPath) && fieldIndex >= unrollFieldNames.length && fieldIndex < fieldNames.length - 1) {
                        unFoldedArray = true;
                    }
                }
            }
        }
        dataFlowType = DataFlowTypesUtils.cloneType(dataFlowType);
        if (unFoldedArray) {
            dataFlowType = new ArrayDataFlowType(dataFlowType);
        }
        return typeFound ? dataFlowType : new AnyDataFlowType();
    }

    public static splitComplexFieldPath(path: string = ''): string[] {
        const pathParts = path.split('.');
        const actualParts: string[] = [];
        let isIncomplete = false;
        pathParts.forEach((processingPart) => {
            if (isIncomplete) {
                actualParts[actualParts.length - 1] += `.${processingPart}`;
            } else {
                actualParts.push(processingPart);
            }
            const currentPart = actualParts[actualParts.length - 1];
            isIncomplete = currentPart.startsWith('{') && !currentPart.endsWith('}');
            if (!isIncomplete && currentPart.endsWith('}')) {
                actualParts[actualParts.length - 1] = currentPart.substring(1, currentPart.length - 1);
            }
        });
        return actualParts;
    }

    public static getFieldsBetweenPaths(baseEntity: IBasedOnEntity, startPath: string, endPath: string): string[] {
        const dataFlowType: IDataFlowDataType = baseEntity
            ? baseEntity.dataFlowColumnType instanceof ArrayDataFlowType && baseEntity.dataFlowColumnType.arrayType instanceof ComplexDataFlowType
                ? baseEntity.dataFlowColumnType.arrayType : baseEntity.dataFlowColumnType
            : new AnyDataFlowType();
        if (dataFlowType instanceof ComplexDataFlowType) {
            const startPaths = ComplexTypeUtils.splitComplexFieldPath(startPath).slice(1);
            const endPaths = ComplexTypeUtils.splitComplexFieldPath(endPath).slice(1);
            const baseName = DataFlowUtils.needToEscape(baseEntity.name) ? `{${baseEntity.name}}` : baseEntity.name;
            return ComplexTypeUtils.getFieldsBetweenPathsRecursion(startPaths, endPaths, baseName, dataFlowType);
        }

        return baseEntity ? [baseEntity.name] : [];
    }

    private static getFieldsBetweenPathsRecursion(startPathNames: string[], fieldNames: string[] = [], prefix: string, dataFlowType: IDataFlowDataType): string[] {
        const fieldsArray: string[] = [];
        if (dataFlowType instanceof ComplexDataFlowType) {
            if (fieldNames.length) {
                dataFlowType.fields.forEach((field) => {
                    const fieldName = DataFlowUtils.needToEscape(field.name) ? `{${field.name}}` : field.name;
                    const newPrefix = `${prefix}.${fieldName}`;
                    if (field.entityName === fieldNames[0]) {
                        const newdataFlowType = field.dataFlowExpression.returnType instanceof ArrayDataFlowType
                            ? field.dataFlowExpression.returnType.arrayType : field.dataFlowExpression.returnType;
                        fieldsArray.push(...ComplexTypeUtils.getFieldsBetweenPathsRecursion(startPathNames.slice(1), fieldNames.slice(1), newPrefix, newdataFlowType));
                    } else if (!startPathNames.length) {
                        fieldsArray.push(newPrefix);
                    }
                });
            } else {
                fieldsArray.push(...dataFlowType.fields.map((field) => `${prefix}.${DataFlowUtils.needToEscape(field.name) ? `{${field.name}}` : field.name}`));
            }
        } else if (!fieldNames.length) {
            fieldsArray.push(prefix);
        }
        return fieldsArray;
    }
}
